import Button from '@mui/material/Button'
import CardContent from '@mui/material/CardContent'
import { DataEditContextTextField } from 'Components/reusable/DataContext/fields/DataEditContextTextField'
import { DataEditContextValidation } from 'Components/reusable/DataContext/Validation'
import { getForgotPasswordInitialData } from 'Components/reusable/DataContext/InitialData'
import { forgotPasswordSchema } from 'Components/reusable/DataContext/ValidationSchema'
import { authAPI } from 'api/AuthAPI'
import { IForgotPassword } from 'types/accountInterfaces'
import InformativeModal from './InformativeModal'
import Grid from '@mui/material/Grid2'
import ErrorOverlay from 'Components/General/ErrorOverlay'
import DataContext, { NewOrExisting } from 'Components/reusable/DataContext'
import LoadingIndicator from 'Components/reusable/LoadingIndicator'
import { useLogin, useUser } from 'hooks'
import React, { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router'
import { IError } from 'types/error'
import CaleoPrimaryButton from 'Components/reusable/Buttons/CaleoPrimaryButton'
import LoginPageCard from 'Components/reusable/CaleoCustomComponents/LoginPageCard'
import { useTranslation } from 'react-i18next'
import caleoLogo from 'assets/images/caleoLogo.svg'

type T = IForgotPassword

/**
 * Renders the ForgotPassword component.
 *
 * @return {React.FC} The rendered component.
 * @notExported
 */
const ForgotPassword: React.FC = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [submitError, setSubmitError] = useState<IError>()
  const [isResetAsked, setIsResetAsked] = useState<boolean>(false)

  const { setResetPassword } = useLogin()

  const [data, setData] = useState<NewOrExisting<T>>(getForgotPasswordInitialData())
  const schema = forgotPasswordSchema()
  const localeBase = 'forgotPassword'
  const [submitLocally, setSubmitLocally] = useState<boolean>(false)
  const [backendError, setBackendError] = useState<IError>()
  const { loggingIn, loggedIn, loginLoading } = useLogin()
  const location = useLocation()
  const { groupsReady, groups } = useUser()

  const buttonsEnabled = !loggingIn

  useEffect(() => {
    if (loggedIn && groupsReady) {
      if (location.state?.url) {
        navigate(location.state?.url)
      } else if (groups && groups.includes('sales')) {
        navigate('/employees')
      } else {
        navigate('/profile')
      }
    }
  }, [loggedIn, groupsReady, location.state?.url, groups, navigate])

  useEffect(() => {
    const controller = new AbortController()

    ;(async () => {
      if (submitLocally) {
        setResetPassword(true)
        try {
          await authAPI.forgotPassword(data, controller)
          setIsResetAsked(true)
        } catch (err) {
          setSubmitError(err as IError)
          setSubmitLocally(false)
        }
      }
    })()

    return () => {
      controller.abort()
    }
  }, [data, setResetPassword, submitLocally])

  if (!data) {
    return <LoadingIndicator />
  }

  if (backendError && backendError.name !== 'CanceledError' && backendError.name !== 'AbortError') {
    return <ErrorOverlay error={backendError} setOpen={setBackendError} />
  }

  if (submitError) {
    return <ErrorOverlay error={submitError} setOpen={setSubmitError} />
  }

  const onClose = () => {
    setResetPassword(false)
    navigate('/login', { state: { search: '' } })
  }

  return (
    <Grid container justifyContent="center" alignItems="center">
      <Grid size={12}>
        <img src={caleoLogo} alt="logo" style={{ width: '159px', padding: '18px' }} />
      </Grid>
      {loginLoading ? (
        <LoadingIndicator />
      ) : (
        <DataContext
          data={data}
          onChange={setData}
          onSubmit={() => setSubmitLocally(true)}
          schema={schema}
          localeBase={localeBase}
        >
          <Grid container justifyContent="center" alignItems="center" size={12}>
            <Grid size={{ xs: 12, sm: 8, md: 6, lg: 4, xl: 3 }}>
              <LoginPageCard header={t('forgotPassword')}>
                <CardContent sx={{ minWidth: '100%', padding: 0 }}>
                  <Grid sx={{ marginTop: '16px', marginBottom: '16px' }} size={12}>
                    <DataEditContextTextField<IForgotPassword> field="email" required fullWidth type="email" />
                    {isResetAsked && (
                      <InformativeModal
                        header={t('resetasked-header')}
                        allowSubmit={t('resetasked-ok')}
                        onClose={onClose}
                        goBack={() => {
                          setResetPassword(false)
                          navigate('/login', { state: { search: '' } })
                        }}
                        maxWidth="sm"
                        fullWidth
                        noEscClose
                      />
                    )}
                  </Grid>

                  <Grid container alignItems="center" spacing={1} justifyContent="space-between" size={12}>
                    <Grid size="auto">
                      <Button
                        data-testid="button-forgot-password-back"
                        onClick={() => navigate('/login')}
                        size="small"
                        variant="contained"
                        color="primary"
                      >
                        {t('profile.backButton')}
                      </Button>
                    </Grid>
                    <Grid size="auto">
                      <DataEditContextValidation>
                        {({ valid }) => (
                          <CaleoPrimaryButton
                            testId="button-forgot-password-submit"
                            valid={valid || !buttonsEnabled}
                            clickAction={() => setSubmitLocally(true)}
                            label={t('submit')}
                            loading={false}
                          />
                        )}
                      </DataEditContextValidation>
                    </Grid>
                  </Grid>
                </CardContent>
              </LoginPageCard>
            </Grid>
          </Grid>
        </DataContext>
      )}
    </Grid>
  )
}

export default ForgotPassword
